import { Geometry } from 'geojson';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { AcresFeatureFlags } from '@/constants/constants';
import {
  COMPS_URL,
  LISTINGS_URL,
  SAVED_LAND_PLAT_MAP_URL,
} from '@/constants/urls';
import { useInsightsCatalogContext } from '@/features/insightsCatalog/provider';
import { ToolNoneDrawModes } from '@/features/toolbar/constants';
import { useToolbarContext } from '@/features/toolbar/provider';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import useUpdatedRef from '@/hooks/useUpdatedRef';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

import ActiveTabTitle from '../models/navigation/activeTab';
import { RootState } from '../redux/reducers';
import { findPolylabel } from '../utils/common';
import flyToLocation from '../utils/flyToLocation';
import getMapZoom from '../utils/getMapZoom';
import toggleVisibility from '../utils/toggleVisibility';
import { AcresMapboxLayerIds, ZOOM_LEVELS } from '.';
import { advancedSearchParcelDots } from './corelogic';
import { countyFillLayer } from './county/county';
import { countyLabelsLayer } from './county/county-label';
import { stateFillLayer, stateHighlightLayer } from './states';
import {
  townshipFillLayer,
  townshipLabelLayer,
  townshipLineLayer,
} from './trs/townships';

type RenderedFeatures = {
  id: string;
  layer: {
    id:
      | AcresMapboxLayerIds.STATE_FILL
      | AcresMapboxLayerIds.COUNTY_FILL
      | AcresMapboxLayerIds.TOWNSHIP_FILL
      | AcresMapboxLayerIds.SECTION_FILL;
    maxzoom: number;
    minzoom: number;
    source: string;
    type: string;
  };
  properties: {
    STUSPS: string;
    GEOID: string;
    NAME: string;
  };
  geometry: Geometry;
};

const useZoomSelect = () => {
  const isToolbarActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_TOOL_TOP_BAR
  );
  const { selectedTool, isDrawToolActive: isDrawingNew } = useToolbarContext();
  const { drawing: drawingRedux } = useSelector(
    (state: RootState) => state.draw
  );

  const drawing = useMemo(() => {
    return isToolbarActive ? isDrawingNew : drawingRedux;
  }, [isDrawingNew, drawingRedux, isToolbarActive]);

  const { parcelsLayerDisabled } = useInsightsCatalogContext();
  const { isUsingQuickMeasure, mapInstance: map } = useMapboxContext();

  const pathname = usePathname();
  const pathnameRef = useUpdatedRef<string | null>(pathname);

  const { isDrawing: isDrawingRedux } = useSelector(
    (state: RootState) => state.drawV2
  );

  const isDrawing = useMemo(() => {
    return isToolbarActive ? isDrawingNew : isDrawingRedux;
  }, [isDrawingNew, isDrawingRedux, isToolbarActive]);
  const isDrawingRef = useUpdatedRef<boolean>(isDrawing);

  const { activeLayer } = useSelector((state: RootState) => state.reportPanel);
  const activeLayerRef = useUpdatedRef<ActiveTabTitle | undefined>(
    activeLayer?.layer
  );

  const state = useRef<string>('');
  const layer = useRef<string>('');

  // Handles flying to state/county/trs
  // can't use callback with mapbox functions will trigger loop (lag)
  const onMapClick = useCallback(
    (e) => {
      const isDisabledForThisPage =
        pathnameRef.current?.includes(COMPS_URL) ||
        pathnameRef.current?.includes(SAVED_LAND_PLAT_MAP_URL) ||
        pathnameRef.current?.includes(LISTINGS_URL) ||
        (isToolbarActive && selectedTool?.mode !== ToolNoneDrawModes.Info_Tool);

      if (
        !map ||
        isDrawingRef.current ||
        isDisabledForThisPage ||
        activeLayerRef.current === ActiveTabTitle.TIMBER_MILLS ||
        parcelsLayerDisabled ||
        isUsingQuickMeasure ||
        e.originalEvent.cancelBubble
      ) {
        return;
      }

      const res = map.queryRenderedFeatures(e.point);
      // Returns if any of the layers contain these ids
      const priorityLayers = [
        'draw',
        advancedSearchParcelDots.id,
        AcresMapboxLayerIds.PORTFOLIO_CENTROIDS,
        AcresMapboxLayerIds.TIMBER_MILLS,
      ];
      const shouldReturn = res.some((feature) =>
        priorityLayers.some((id) => feature.layer.id.includes(id))
      );

      if (shouldReturn) return;

      const rad = 3;
      const bbox = [
        [e.point.x - rad, e.point.y - rad],
        [e.point.x + rad, e.point.y + rad],
      ];
      const layerFeatures: RenderedFeatures[] = map.queryRenderedFeatures(
        bbox as any,
        {
          layers: [
            AcresMapboxLayerIds.STATE_FILL,
            AcresMapboxLayerIds.COUNTY_FILL,
            AcresMapboxLayerIds.TOWNSHIP_FILL,
            AcresMapboxLayerIds.SECTION_FILL,
          ],
        }
      ) as any;

      if (layerFeatures.length) {
        const firstFeature = layerFeatures[0];
        const center = findPolylabel({
          geometry: firstFeature.geometry,
          properties: {},
          type: 'Feature',
        });

        switch (firstFeature.layer.id) {
          case AcresMapboxLayerIds.STATE_FILL: {
            flyToLocation(map, center as any, ZOOM_LEVELS.STATES.MAX);
            break;
          }
          case AcresMapboxLayerIds.COUNTY_FILL: {
            flyToLocation(map, center as any, ZOOM_LEVELS.COUNTIES.MAX - 1);
            break;
          }
          case AcresMapboxLayerIds.TOWNSHIP_FILL: {
            flyToLocation(map, center as any, ZOOM_LEVELS.TOWNSHIPS.MAX - 1);
            break;
          }
          case AcresMapboxLayerIds.SECTION_FILL: {
            const mapZoom = getMapZoom(map);
            if (mapZoom < ZOOM_LEVELS.PARCELS.MIN) {
              flyToLocation(map, center as any, ZOOM_LEVELS.SECTIONS.MAX - 0.5);
            }

            break;
          }
          default: {
            break;
          }
        }
      }
    },
    [isUsingQuickMeasure, map, parcelsLayerDisabled]
  );

  useEffect(() => {
    if (!map) return () => {};

    map.on('click', AcresMapboxLayerIds.STATE_FILL, onMapClick);
    map.on('click', AcresMapboxLayerIds.COUNTY_FILL, onMapClick);
    map.on('click', AcresMapboxLayerIds.TOWNSHIP_FILL, onMapClick);
    map.on('click', AcresMapboxLayerIds.SECTION_FILL, onMapClick);

    toggleVisibility(
      map,
      [
        stateHighlightLayer,
        stateFillLayer,
        countyFillLayer,
        countyLabelsLayer,
        townshipFillLayer,
        townshipLineLayer,
        townshipLabelLayer,
        AcresMapboxLayerIds.SECTION_FILL,
        AcresMapboxLayerIds.SECTION_LINE,
        AcresMapboxLayerIds.SECTION_LABELS,
      ],
      !drawing
    );

    if (drawing) {
      map.off('click', onMapClick);
    }

    return () => {
      map.off('click', onMapClick);
      map.off('click', AcresMapboxLayerIds.STATE_FILL, onMapClick);
      map.off('click', AcresMapboxLayerIds.COUNTY_FILL, onMapClick);
      map.off('click', AcresMapboxLayerIds.TOWNSHIP_FILL, onMapClick);
      map.off('click', AcresMapboxLayerIds.SECTION_FILL, onMapClick);
    };
  }, [map, drawing, layer, state, onMapClick]);
};

export default useZoomSelect;
